<template>
  <div class="picture-container">
    <picture ref="thisPicture">
      <template v-if="responsive && isValidImageUrl">
        <template
          v-for="(media, breakpointName) in mediaMapByBreakPoints"
          :key="breakpointName"
        >
          <template
            v-for="imgType in imgTypes"
            :key="imgType"
          >
            <source
              :media="media"
              :type="getSrcType(imgType)"
              :srcset="getSrcSet({
                breakpointName: breakpointName,
                imageType: imgType,
              })"
            >
          </template>
        </template>
      </template>
      <template v-else-if="!responsive && isValidImageUrl">
        <template
          v-for="imgType in imgTypes"
          :key="imgType"
        >
          <source
            :type="getSrcType(imgType)"
            :srcset="getSrcSet({ imageType: imgType })"
          >
        </template>
      </template>
      <img
        v-if="isValidImageUrl && !hasError"
        ref="imageRef"
        :alt="alt"
        :data-src="imageUrl"
        :loading="lazyLoad ? 'lazy' : null"
        :src="imgSrc()"
        :class="['picture-image', { loaded: isLoaded }, imageClass]"
        :fetchpriority="currentFetchPriority"
        :draggable="draggable"
        :width="imageIntrinsicWidth ? Math.round(imageIntrinsicWidth) : 'auto'"
        :height="imageIntrinsicWidth ? Math.round(imageIntrinsicWidth / aspectRatio) : 'auto'"
        @load="onImageLoaded"
        @error="onImageUrlError"
      >
      <div
        v-else-if="!isValidImageUrl || hasError"
        ref="imageFB"
        role="img"
        :alt="alt || 'fallback image'"
        :aria-label="alt || 'fallback image'"
        :style="`width:${updateFBImg().imgWidthFB};padding-top:${updateFBImg().padding};margin:auto;`"
        class="grid-x picture-image picture-fallback-background"
      >
        <div class="picture-fallback-content">
          <span class="picture-fallback-icon" />
        </div>
      </div>
    </picture>
  </div>
</template>

<script>
/* istanbul ignore file */
import {
  defineComponent,
  ref,
  onMounted,
  computed,
} from 'vue';
import observe from '@common-vue/atomic-ui/src/utils/lazyLoadObserver';

export default defineComponent({
  name: 'Picture',
  props: {
    fetchPriority: {
      type: [Boolean, String],
      default: false,
      validator: (val) => [true, false, 'high', 'low', 'auto'].includes(val),
    },
    imageUrl: {
      type: String,
      default: '',
    },
    imageClass: {
      type: String,
      default: '',
    },
    imageIntrinsicWidth: {
      type: Number,
      default: null,
    },
    alt: {
      type: String,
      default: '',
    },
    imageSizesByBreakpoints: {
      type: Object,
      default: () => ({}),
    },
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    imageType: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: Number,
      default: 0.8,
    },
    draggable: {
      type: String,
      default: 'auto',
      validator: (val) => ['true', 'false', 'auto'].includes(val),
    },
  },
  setup(props) {
    const thisPicture = ref(null);
    const lazyLoaded = ref(props.fetchPriority === true || props.fetchPriority === 'high');

    const mediaMapByBreakPoints = {
      small: '(max-width: 599px)',
      medium: '(min-width: 600px) and (max-width: 1023px)',
      large: '(min-width: 1024px) and (max-width: 1279px)',
      mlarge: '(min-width: 1280px) and (max-width: 1599px)',
      xlarge: '(min-width: 1600px)',
    };
    const responsive = Object.keys(props.imageSizesByBreakpoints).length;

    const imgWidth = computed(() => {
      if (props.imageIntrinsicWidth === null) {
        return 240;
      }
      return Math.round(props.imageIntrinsicWidth);
    });

    const imageFB = ref();
    const hasError = ref(false);
    const currentWidth = ref(null);
    const imageRef = ref();
    const isLoaded = ref(false);

    const isImage = () => {
      if (props.imageUrl.toLowerCase().startsWith('/')) {
        return true;
      }

      try {
        return Boolean(new URL(props.imageUrl));
      } catch (e) {
        return false;
      }
    };
    const isValidImageUrl = computed(() => props.imageUrl !== '' && isImage());

    const onImageUrlError = () => {
      hasError.value = true;
    };

    const currentFetchPriority = computed(() => {
      switch (props.fetchPriority) {
      case true: return 'high';
      case false: return null;
      default: return props.fetchPriority;
      }
    });

    const getBackboneImgUrl = ({
      breakpointName = 'medium',
      imageType = props.imageType || 'jpeg',
    }) => {
      const url = new URL(props.imageUrl);
      const width = props.imageSizesByBreakpoints[breakpointName];
      return `${url}?op_sharpen=1&wid=${width}&fit=fit,1&fmt=${imageType}`;
    };
    /**
    * @param {{ breakpointName: string, imageType:string }} param
    * @return {null | string}
    */
    const getSrcSet = ({ breakpointName, imageType } = { breakpointName: '', imageType: '' }) => {
      if (!lazyLoaded.value) {
        return null;
      }
      if (responsive) {
        return `${getBackboneImgUrl({ breakpointName, imageType })}`;
      }
      return props.imageUrl;
    };
    /**
     * @param {string} imageType
     * @returns {string}
     */
    const getSrcType = (imageType) => (imageType === 'webp-alpha' ? 'image/webp' : `image/${imageType}`);

    const setupIntersect = () => {
      const ele = thisPicture.value;
      if (!lazyLoaded.value) {
        observe(ele, () => {
          lazyLoaded.value = true;
        });
      }
    };

    const onImageLoaded = () => {
      isLoaded.value = true;
    };

    onMounted(() => {
      if (!props.lazyLoad) {
        lazyLoaded.value = true;
      }
      setupIntersect();
    });

    const updateFBImg = () => {
      const imageFBStyle = {
        imgWidthFB: '',
        padding: '',
      };
      if (props.imageIntrinsicWidth === null) {
        imageFBStyle.imgWidthFB = '100%';
        imageFBStyle.padding = `${(1 / props.aspectRatio) * 100 }%`;
      } else {
        imageFBStyle.imgWidthFB = `${props.imageIntrinsicWidth }px`;
        imageFBStyle.padding = `${(1 / props.aspectRatio) * props.imageIntrinsicWidth }px`;
      }
      return imageFBStyle;
    };

    const imgTypes = (props.imageType) ? [props.imageType] : ['webp', 'jpeg'];

    const imgSrc = () => {
      if (typeof window === 'undefined') {
        return props.lazyLoad ? null : props.imageUrl;
      }
      if (lazyLoaded.value) {
        return props.imageUrl;
      }
      if (!lazyLoaded.value) {
        return `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${
            imgWidth.value
        }px' height='${Math.round(imgWidth.value / props.aspectRatio)}px' />`;
      }
      return props.imageUrl;
    };

    return {
      mediaMapByBreakPoints,
      thisPicture,
      lazyLoaded,
      imgTypes,
      imgWidth,
      imageFB,
      responsive,
      isValidImageUrl,
      hasError,
      imageRef,
      currentWidth,
      isLoaded,
      currentFetchPriority,
      // setupIntersect,
      getSrcSet,
      getSrcType,
      updateFBImg,
      onImageLoaded,
      imgSrc,
      onImageUrlError,
    };
  },
});
</script>

<style lang="scss" scoped>
.picture-container{
  position: relative;
  .picture-position{
    position: absolute;
  }
}
.picture-image {
  width: 100%;
}
.picture-fallback-background {
  background: $hover-color;
  position:relative;
}
:deep(.picture-fallback-content) {
  margin: auto;
}
:deep(.picture-fallback-content) {
  position:absolute;
  top:50%;
  transform:translateY(-50%);
  width:100%;
  text-align:center;
}
.picture-fallback-text {
  font-weight: $font-weight-sm;
  font-size: $font-size-sm;
  line-height: $line-height-sm;
  text-align: center;
}

.lazy-load-placeholder {
  height: 0px;
  position: relative;
  width: 100%;
  img {
    display: block;
    min-height: 1px;
    position: absolute;
    visibility: hidden;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    transition: opacity 0.15s ease-in;
    opacity: 0;
    width: 100%;
    &.loaded {
      visibility: visible;
      opacity: 1;
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "~@csgIcons/_picture-grey.scss";

.picture-fallback-icon {
  @extend .picture-grey;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "~@csgIcons/_picture-grey-filled.scss";

.picture-fallback-icon {
  @extend .picture-grey-filled;
}
</style>
